import { getApiV1Url } from '@/config/api'

/** 서버 공통 응답 (data만 사용) */
interface ApiResponse<T> {
  data?: T
  errors?: {
    error?: Array<{ code?: number; target?: string; message?: string }>
  }
}

/** 쿠폰 상태 */
export type CouponStatus =
  | 'PENDING'
  | 'VALID'
  | 'USED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'SUSPENDED'

/** 쿠폰 내역 화면에서 쓰는 상태(현재 UI 기준) */
export type CouponHistoryUiStatus = 'active' | 'used' | 'expired' | 'cancelled' | 'suspended'

/** status 한글 라벨(요구사항 확정 반영) */
export const COUPON_STATUS_LABEL: Record<CouponStatus, string> = {
  PENDING: '발급완료(비활성)',
  VALID: '발급완료',
  USED: '사용완료',
  CANCELED: '취소',
  EXPIRED: '만료',
  SUSPENDED: '사용정지'
}

/** API status -> 쿠폰내역 UI status 매핑 */
export function toCouponHistoryUiStatus(status: CouponStatus): CouponHistoryUiStatus {
  switch (status) {
    case 'VALID':
      return 'active'
    case 'PENDING':
      // 정책: PENDING은 active로 표시(라벨만 비활성)
      return 'active'
    case 'USED':
      return 'used'
    case 'EXPIRED':
      return 'expired'
    case 'CANCELED':
      return 'cancelled'
    case 'SUSPENDED':
      // 정책: cancelled로 뭉개지 않고 별도 라벨
      return 'suspended'
    default: {
      // 타입이 좁혀졌지만, 런타임 안전장치로 active fallback
      return 'active'
    }
  }
}

/** 쿠폰 목록 응답의 쿠폰 한 건 */
export interface CouponItemDto {
  issuedShopName: string
  couponCode: string
  status: CouponStatus
  couponType: string
  couponName: string
  discountTarget: string
  discountType: string
  discountValue: number
  maxDiscount?: number
  minPayment?: number
  issuedAt: string
  expireAt: string
  couponImageUrl?: string
  usedAt?: string
  usedShopName?: string
  /** 사용 시 주문 번호(사용 취소 API의 usedOrderNo) */
  usedOrderNo?: string
  transactionId?: string
}

/** 쿠폰 목록 응답 data */
export interface CouponListData {
  totalCount: number
  coupons: CouponItemDto[]
  totalPages: number
  currentPage: number
  pageSize: number
  hasNext: boolean
  hasPrevious: boolean
}

/** 쿠폰 목록 API 요청 파라미터 */
export interface GetCouponsParams {
  customerCode?: string
  issuedShopCode?: string
  status?: string
  keyword?: string
  recentDays?: number
  issuedAtStart?: string
  issuedAtEnd?: string
  usedShopCode?: string
  usedOrderNo?: string
  limit?: number
  page?: number
  size?: number
  sort?: string
}

function toQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return
    if (typeof v === 'string' && v.trim() === '') return
    qs.set(k, String(v))
  })
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/coupons
 */
export async function getCoupons(
  projectCode: string,
  shopCode: string,
  params: GetCouponsParams
): Promise<CouponListData> {
  const query = toQueryString(params as Record<string, unknown>)
  const url = getApiV1Url(`/projects/${projectCode}/shops/${shopCode}/coupons${query}`)
  const response = await fetch(url)
  const json: ApiResponse<CouponListData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('쿠폰 목록을 불러올 수 없습니다.')
  }

  return json.data
}

/** 회원대시보드 > 미사용 쿠폰 (status=VALID, limit=5) */
export async function getUnusedCouponsTop5(input: {
  projectCode: string
  shopCode: string
  customerCode: string
  issuedShopCode: string
}): Promise<CouponItemDto[]> {
  const data = await getCoupons(input.projectCode, input.shopCode, {
    customerCode: input.customerCode,
    issuedShopCode: input.issuedShopCode,
    status: 'VALID',
    limit: 5,
    sort: 'issuedAt,DESC'
  })
  return Array.isArray(data.coupons) ? data.coupons : []
}

/** 회원대시보드 > 최근 쿠폰 내역 5개 (issuedAt 최신순) */
export async function getRecentCouponHistoryTop5(input: {
  projectCode: string
  shopCode: string
  customerCode: string
  issuedShopCode: string
}): Promise<CouponItemDto[]> {
  const data = await getCoupons(input.projectCode, input.shopCode, {
    customerCode: input.customerCode,
    issuedShopCode: input.issuedShopCode,
    limit: 5,
    sort: 'issuedAt,DESC'
  })
  return Array.isArray(data.coupons) ? data.coupons : []
}

/** 쿠폰 내역 > recentDays=93 + 페이징 */
export async function getCouponHistoryPaged(input: {
  projectCode: string
  shopCode: string
  customerCode: string
  issuedShopCode: string
  recentDays?: number
  page: number
  size: number
  sort?: string
}): Promise<CouponListData> {
  return await getCoupons(input.projectCode, input.shopCode, {
    customerCode: input.customerCode,
    issuedShopCode: input.issuedShopCode,
    recentDays: input.recentDays ?? 93,
    page: input.page,
    size: input.size,
    sort: input.sort ?? 'issuedAt,DESC'
  })
}

export interface PostCouponUseRequest {
  brandCode: string
  customerCode: string
  usedOrderNo: string
}

export interface CouponUseData {
  usedAt: string
  usedShopCode: string
  usedOrderNo: string
}

/**
 * POST /api/v1/projects/{projectCode}/shops/{shopCode}/coupons/{couponCode}/use
 */
export async function postCouponUse(
  projectCode: string,
  shopCode: string,
  couponCode: string,
  body: PostCouponUseRequest
): Promise<CouponUseData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/coupons/${encodeURIComponent(couponCode)}/use`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<CouponUseData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('쿠폰 사용 결과를 확인할 수 없습니다.')
  }

  return json.data
}

export interface PostCouponCancelUseRequest {
  customerCode: string
  usedOrderNo: string
}

export interface CouponCancelUseData {
  status: string
  usedAt: string
  usedShopCode: string
  usedOrderNo: string
}

/**
 * POST /api/v1/projects/{projectCode}/shops/{shopCode}/coupons/{couponCode}/cancel-use
 */
export async function postCouponCancelUse(
  projectCode: string,
  shopCode: string,
  couponCode: string,
  body: PostCouponCancelUseRequest
): Promise<CouponCancelUseData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/coupons/${encodeURIComponent(couponCode)}/cancel-use`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<CouponCancelUseData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('쿠폰 사용 취소 결과를 확인할 수 없습니다.')
  }

  return json.data
}

